import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * `AD-LAY01`, ADR-0071: a page declares what kind of surface it is, not how
 * many pixels wide it should be (`docs/03` §5, `AGENTS.md` §6 rule 16).
 *
 * The numbers live in `--w-*` in `app/globals.css` and reach these classes
 * through the `maxWidth` extension in `tailwind.config.ts`, so each one is
 * written once. `progsity-admin`'s `PageContainer` uses the same five names.
 * Paired with `w-full` every cap is fluid below itself, which is why none of
 * this needs a media query.
 */
const WIDTH_CLASS = {
  /** Reading surfaces: policies, About, long-form copy. Line length is the constraint. */
  read: "max-w-read",
  /** One job, one form: checkout, an author form, a single-purpose page. */
  form: "max-w-form",
  /** Marketing pages, dashboards and detail pages: card grids, stat rows. */
  page: "max-w-page",
  /** Tables, consoles and monitors. Where a wide display actually pays off. */
  board: "max-w-board",
  /** Tools that own the viewport. Gutters only, and a decision, not an omission. */
  full: "max-w-none",
} as const;

export type ContainerWidth = keyof typeof WIDTH_CLASS;

type ContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Which width ladder rung this wrapper sits on (`docs/03` §5).
   *
   * Defaults to `page`, which is what every current caller wants and what the
   * old hard-coded 1200 was reaching for. The site header and the bars above
   * it sit on `page` too (`SITE-NAV01`): they had a 1400 px rung of their own
   * from when pages were 1200, and once pages reached 1680 the logo sat 20 px
   * inside the page's own left edge on a 1440 screen.
   */
  width?: ContainerWidth;
};

export function Container({
  className,
  width = "page",
  ...props
}: ContainerProps) {
  return (
    <div
      className={cn(
        "mx-auto w-full px-6 lg:px-8",
        WIDTH_CLASS[width],
        className
      )}
      {...props}
    />
  );
}
